import { CloseIcon, SearchIcon } from '@chakra-ui/icons';
import { Box, Flex, Input, InputGroup, InputLeftElement, InputRightElement, List, ListItem, Spinner, Text, useColorModeValue} from '@chakra-ui/react';
import { useEffect, useRef, useState } from 'react';
import { useGetTargetCustomAudienceQuery } from 'store/facebookServices/facebookServicesApi';


const shouldRemoveCustomeAudience = (newAudience, existingAudience) => {

    return (
        newAudience.audience_id === existingAudience.audience_id
    );
};

const filterSelectedCustomAudiences = (newAudience, selectedCustomAudiences) => {
    //console.log({ newAudience, selectedCustomAudiences });
    return selectedCustomAudiences.filter((audience) => !shouldRemoveCustomeAudience(newAudience, audience));
};

const IncludeCustomAudience = ({ adsAccountId, onIncludeAudienceChange, preFilledTargets }) => {

    const inputBackground = useColorModeValue("#f8f6fb", "#1e1530");
    const borderColor = useColorModeValue('gray.300', 'gray.500');
    const hoverColor = useColorModeValue('#f0f0f0', 'gray.700');

    const inputRef = useRef(null);
    const popupRef = useRef(null);

    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
    const [customAudienceOptions, setCustomAudienceOptions] = useState([]);
    const [selectedCustomAudiences, setselectedCustomAudiences] = useState(
        preFilledTargets?.length > 0 
            ? preFilledTargets.map(target => ({
                audience_id: target?.id, // Rename `id` to `audience_id`
                name: target?.name
            })) 
            : []
    );

    const [showPopup, setShowPopup] = useState(false);
    const [isInputFocused, setIsInputFocused] = useState(false);

    const { data: customAudiences, isLoading, isFetching, refetch } = useGetTargetCustomAudienceQuery({ searchTerm: debouncedSearchTerm, adsAccountId}, { skip: !isInputFocused });

    useEffect(() => {
        if (searchTerm?.length == 0) {
            setDebouncedSearchTerm('');
            setShowPopup(false);
            return;
        }

        const handler = setTimeout(() => {
            setDebouncedSearchTerm(searchTerm);
        }, 800);

        return () => {
            clearTimeout(handler);
        };
    }, [searchTerm]);

    useEffect(() => {
        if (searchTerm?.length > 0 && (searchTerm == debouncedSearchTerm)) {
            refetch()
        }
    }, [searchTerm, debouncedSearchTerm])


    useEffect(() => {
        if (customAudiences?.data?.length > 0) {
            setShowPopup(true);
            setCustomAudienceOptions(customAudiences?.data);
        } else {
            setShowPopup(false);
        }
    }, [customAudiences]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                inputRef.current &&
                !inputRef.current.contains(event.target) &&
                popupRef.current &&
                !popupRef.current.contains(event.target)
            ) {
                setShowPopup(false);
            }

        };

        document.addEventListener('mousedown', handleClickOutside);

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const handleAudienceSelect = (audience) => {

        setselectedCustomAudiences(prevAudiences => {
            const filteredAudiences = filterSelectedCustomAudiences(audience, prevAudiences);
            const updatedAudiences = [...filteredAudiences, audience]
            setTimeout(() => onIncludeAudienceChange(updatedAudiences), 0);
            return updatedAudiences;
        });

        setSearchTerm('');
        setDebouncedSearchTerm('');
        setShowPopup(false);
        setCustomAudienceOptions([]);
    };

    const removeAudienceEntry = (audience_id) => {
        setselectedCustomAudiences(prevAudiences => {
            const updatedAudiences = prevAudiences.filter(audience => audience.audience_id !== audience_id);
            setTimeout(() => onIncludeAudienceChange(updatedAudiences), 0);
            return updatedAudiences;
        });
    };


    return (
        <Box>
            <Box my={'10px'}>
                {selectedCustomAudiences?.map((audience) => (
                    <Flex key={audience?.audience_id} bg={inputBackground} align={'center'} justify={'space-between'} width={'full'} py={'5px'} px={'10px'} borderRadius={'md'} mb={'5px'}>

                        <Flex flex={1} pr={'10px'} align={'center'}>
                            <Flex justify={'space-between'} flex={1} align={'center'}>
                                <Text ml={'5px'} fontSize={'14px'} fontWeight={'bold'}>{audience?.name} </Text>
                            </Flex>
                        </Flex>
                        <CloseIcon _hover={{ cursor: 'pointer', color: 'red.400' }} boxSize={3} color='gray.500' onClick={() => removeAudienceEntry(audience?.audience_id)} />
                    </Flex>
                ))}
            </Box>
            <Box position="relative">
                <Flex>
                    <InputGroup >
                        <InputLeftElement height={'32px'}>
                            <SearchIcon boxSize={4} color='gray.500' />
                        </InputLeftElement>
                        <Input
                            ref={inputRef}
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            placeholder="Search existing audiences"
                            autoComplete="off"
                            borderRadius={'md'}
                            size={'sm'}
                            onFocus={() => {
                                setIsInputFocused(true);
                                if (customAudienceOptions?.length > 0) {
                                    setShowPopup(true)
                                }
                            }}
                            onBlur={() => {
                                setTimeout(() => {
                                    setIsInputFocused(false);
                                }, 100); 
                            }}                            
                        />
                        <InputRightElement height={'32px'}>
                            {isFetching ? (
                                <Spinner boxSize={3} />
                            ) : (<>
                                {searchTerm?.length > 0 ? (
                                    <CloseIcon boxSize={3} color='gray.500' onClick={() => { setSearchTerm(''); setDebouncedSearchTerm(''); }} />
                                ) : null}
                            </>)}
                        </InputRightElement>
                    </InputGroup>
                </Flex>

                <>
                    {showPopup && customAudienceOptions.length > 0 && (
                        <List
                            ref={popupRef}
                            position="absolute"
                            top="100%"
                            width="100%"
                            border="1px solid"
                            borderColor={borderColor}
                            backgroundColor={inputBackground}
                            zIndex="1000"
                            maxHeight="200px"
                            overflowY="auto"
                        >
                            {customAudienceOptions.map((audience) => (
                                <ListItem
                                    key={audience.audience_id * Math.random() + audience.name}
                                    p={'5px'}
                                    px={'10px'}
                                    cursor="pointer"
                                    _hover={{ backgroundColor: hoverColor }}
                                    onClick={() => handleAudienceSelect(audience)}
                                >
                                    <Flex justify={'space-between'} >
                                        <Text fontSize={'14px'}>
                                            {audience.name}
                                        </Text>
                                        <Text fontSize={'12px'}>
                                            {audience.parent_category}
                                        </Text>
                                    </Flex>
                                </ListItem>
                            ))}
                        </List>
                    )}
                </>
            </Box>

        </Box>
    );
};

export default IncludeCustomAudience;
